<template>
  <ConversationsTimeline
    ref="conversationsTimelineRef"
    style="height: 100%"
    :conversations="conversations"
    :agreement-data="agreement"
    :is-visible="isVisible"
    @reply-to="replyTo"
  />

  <Modal
    :is-open="modalIsOpen"
    :title="newComm.modalTitle"
    :description="'Send an ' + newComm.type + ' to ' + agreement.customer.name"
    :icon="newComm.type == 'call' ? 'phone' : newComm.type == 'email' ? 'envelope' : 'message'"
    :ok-text="newComm.isSending ? 'Sending...' : 'Send'"
    close-text="Cancel"
    :min-width="newComm.type == 'email' ? '700' : '400'"
    overflow-behaviour="visible"
    :has-unsaved-changes="newCommHasUnsavedChanges"
    :is-confirming="newComm.isSending"
    :disable-close="newComm.isSending"
    @close-modal="closeModal"
    @ok-modal="submitNewComm"
  >
    <div v-if="newComm.type == 'call'">To: {{ agreement.customer.contact.mobile }}</div>
    <div v-if="newComm.type == 'email'">
      <div class="flex items-center py-2">
        <div
          class="mr-3 text-secondary"
          :class="{ '!text-danger': v$.email.from.$error }"
        >
          From
        </div>
        <input
          v-model="newComm.email.from"
          v-tippy="{ content: 'Automatically set', placement: 'top-start' }"
          readonly
          :class="{ 'text-error': v$.email.from.$error }"
          class="!bg-transparent w-full cursor-not-allowed"
        />
      </div>
      <Separator />
      <div class="flex items-center py-2">
        <div class="mr-3 text-secondary">To</div>
        <input
          v-model="newComm.email.to"
          class="w-full bg-bg-primary_alt"
          :class="{ 'text-error': v$.email.to.$error }"
          :has-error="v$.email.to.$error"
        />
      </div>
      <Separator />
      <div class="flex items-center py-2">
        <div
          class="mr-3 text-secondary"
          :class="{ 'text-error': v$.email.subject.$error }"
        >
          Subject
        </div>
        <input
          v-model="newComm.email.subject"
          :class="{ 'text-error': v$.email.to.$error }"
          class="w-full bg-bg-primary_alt mr-3"
        />
        <Dropdown
          class="mt-0"
          position="bottom-right"
        >
          <template #triggerContent>
            <div class="flex items-center cursor-pointer">
              <Icon
                type="sparkles"
                class="mr-1"
              />
              Templates
              <Icon
                type="chevron-down"
                class="ml-1"
              />
            </div>
          </template>
          <DropdownItemGroup
            v-for="category in commTemplatesOrdered"
            :key="category.name"
            :text="category.name"
          >
            <DropdownItem
              v-for="template in category.items"
              :key="template.id"
              @item-clicked="applyTemplate(template.id)"
            >
              {{ template.name }}
            </DropdownItem>
          </DropdownItemGroup>
        </Dropdown>
      </div>
      <Separator />

      <div class="py-2">
        <textarea
          v-model="newComm.email.body"
          :class="{ '!placeholder-danger text-error': v$.email.body.$error }"
          class="w-full bg-bg-primary_alt"
          placeholder="Body"
          :rows="10"
        ></textarea>
        <!-- <Button
          v-if="newComm.replyingToBody"
          label="Get AI Response"
          severity="secondary"
          outlined
          icon="sparkles"
          :is-loading="aiEmailResponderApiRequest.isLoading.value"
          @click="getAiEmailResponse(agreement.id, newComm.replyingToBody)"
        /> -->
      </div>
    </div>
    <div v-if="newComm.type == 'sms'">
      <div class="grid grid-cols-2 gap-8">
        <FormGroup>
          <Label>From</Label>
          <Input
            v-model="newComm.sms.from"
            readonly
            :has-error="v$.sms.from.$error"
          >
            {{ newComm.sms.from }}
          </Input>
          <InputError :has-error="v$.sms.from.$error">{{ v$.sms.from.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup>
          <Label>To</Label>
          <Input
            v-model="newComm.sms.to"
            :has-error="v$.sms.to.$error"
          >
            {{ newComm.sms.to }}
          </Input>
          <InputError :has-error="v$.sms.to.$error">{{ v$.sms.to.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
      <FormGroup>
        <Label>Message</Label>
        <Textarea
          v-model="newComm.sms.body"
          :has-error="v$.sms.body.$error"
        >
          {{ newComm.sms.body }}
        </Textarea>
        <InputError :has-error="v$.sms.body.$error">{{ v$.sms.body.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <Dropdown class="mt-3">
        <template #triggerContent>
          <Button
            label="Templates"
            severity="secondary"
            outlined
            icon="sparkles"
            is-dropdown
          />
        </template>
        <DropdownItemGroup
          v-for="category in commTemplatesOrdered"
          :key="category.name"
          :text="category.name"
        >
          <DropdownItem
            v-for="template in category.items"
            :key="template.id"
            @item-clicked="applyTemplate(template.id)"
          >
            {{ template.name }}
          </DropdownItem>
        </DropdownItemGroup>
      </Dropdown>
    </div>
    <InputError
      :has-error="v$.email.$error"
      class="text-center w-full"
    >
      {{ v$.email.$errors[0]?.$message }}
    </InputError>
  </Modal>
</template>

<script setup>
// vue
import { ref, inject, toRef, watch, computed } from 'vue'
// components
import ConversationsTimeline from '@/components/conversations/timeline/ConversationsTimeline.vue'
import Modal from '@/components/modal/Modal.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Label from '@/components/forms/Label.vue'
import Textarea from '@/components/forms/TextArea.vue'
import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'
import Separator from '@/components/separator/Separator.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import DropdownItemGroup from '@/components/dropdown/DropdownItemGroup.vue'
// composables
import useAgreementPlaceholders from '@/composables/resources/useAgreementPlaceholders'
import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useCallCenterStore } from '@/stores/CallCenterStore'
// utilities
import { alertToast } from '@/utilities/notification'
import { textToHtml } from '@/utilities/string'
// validators
import useVuelidate from '@vuelidate/core'
import { email, requiredIf, helpers } from '@vuelidate/validators'
const props = defineProps({
  agreement: {
    type: Object,
    required: true,
    default: null
  },
  conversations: {
    type: Object,
    default: () => ({})
  },
  isVisible: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['new-conversation-submitted'])

const conversationsTimelineRef = ref(null)

/**
 * Scrolls conversations to bottom
 * Exposed for parent components to call
 */
function scrollToBottom() {
  if (conversationsTimelineRef.value && typeof conversationsTimelineRef.value.scrollToBottom === 'function') {
    conversationsTimelineRef.value.scrollToBottom()
  }
}

// Expose method for parent components
defineExpose({
  scrollToBottom
})
const emitter = inject('emitter')
const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()

const emailApiRequest = useApiRequest()
const smsApiRequest = useApiRequest()

const agreement = toRef(props, 'agreement')

const selectedCallCenterCommsConfig = computed(() => {
  // If no call center is selected, return null
  if (!callCenterStore.selectedCallCenter || !agreement.value?.dealership?.commsConfig) {
    return null
  }

  const commsConfig = agreement.value?.dealership?.commsConfig

  const selectedConfig = commsConfig?.find(config => config.callCenterId === parseInt(callCenterStore.selectedCallCenter))

  if (!selectedConfig) {
    return null
  }

  return selectedConfig
})

const { initPlaceholders, replacePlaceholders, brToNewLine } = useAgreementPlaceholders(
  agreement,
  selectedCallCenterCommsConfig
)

const newCommInitState = ref()
const newComm = ref({
  type: null,
  call: {},
  email: { to: '', from: '', subject: '', body: '' },
  sms: { to: '', from: '', body: '' }
})
const newCommHasUnsavedChanges = ref(false)

const commTemplates = ref([])
const commTemplatesApiRequest = useApiRequest()

// Only fetch templates if not already loaded
if (commTemplates.value.length === 0 && !commTemplatesApiRequest.isLoading.value) {
  commTemplatesApiRequest
    .send({ endpoint: '/v1/comms/canned?usedFor=communication&types=email,sms', method: 'GET' })
    .then(response => {
      if (response.success) {
        commTemplates.value = response.data
      }
    })
}

// const { commTemplates, loadCommTemplates } = getCommTemplates({
//   types: ['email', 'sms']
//   // dealershipID: props.agreement.dealership.id
// })
// loadCommTemplates()

const commTemplatesOrdered = computed(() => {
  // Remove templates which are not equal to the selected contact method (newComm.type)
  let filtered = commTemplates.value.filter(template => template.type == newComm.value.type)

  // Remove irrelevant categories. If PCP, remove HP and CH. If HP, remove PCP and CH. If CH, remove PCP and HP.
  if (agreement.value?.productType == 'PCP') {
    filtered = filtered.filter(template => template.category !== 'HP' && template.category !== 'CH')
  }
  if (agreement.value?.productType == 'HP') {
    filtered = filtered.filter(template => template.category !== 'PCP' && template.category !== 'CH')
  }
  if (agreement.value?.productType == 'CH') {
    filtered = filtered.filter(template => template.category !== 'PCP' && template.category !== 'HP')
  }

  const categories = [...new Set(filtered.map(item => item.category))]
  const ordered = []
  categories.forEach(category => {
    const items = filtered.filter(item => item.category == category)
    ordered.push({ name: category, items: items })
  })
  return ordered
})

const inputRules = {
  email: {
    from: {
      required: helpers.withMessage(
        'Sender is required',
        requiredIf(function () {
          return newComm.value.type == 'email'
        })
      ),
      email: helpers.withMessage(
        'Invalid sender email address',
        requiredIf(function () {
          return newComm.value.type == 'email'
        })
      )
    },
    to: {
      required: helpers.withMessage(
        'Recipient is required',
        requiredIf(function () {
          return newComm.value.type == 'email'
        })
      ),
      email: helpers.withMessage('Invalid recipient email address', email)
    },
    subject: {
      required: helpers.withMessage(
        'Email subject is required',
        requiredIf(function () {
          return newComm.value.type == 'email'
        })
      )
    },
    body: {
      required: helpers.withMessage(
        'Email body is required',
        requiredIf(function () {
          return newComm.value.type == 'email'
        })
      ),
      containsPlaceholders: helpers.withMessage('The email body contains placeholders {{ }}', value => {
        // If the type is not email, always return true (meaning no error for this rule)
        if (newComm.value.type !== 'email') {
          return true
        }

        // If the type is email and the value includes {{ return false (meaning there's an error)
        if (value.includes('{{')) {
          return false
        }

        // Otherwise, return true (meaning no error for this rule)
        return true
      })
    }
  },
  sms: {
    from: {
      required: helpers.withMessage(
        'Sender number is required',
        requiredIf(function () {
          return newComm.value.type == 'sms'
        })
      )
    },
    to: {
      required: helpers.withMessage(
        'Recipient number is required',
        requiredIf(function () {
          return newComm.value.type == 'sms'
        })
      )
    },
    body: {
      required: helpers.withMessage(
        'SMS message is required',
        requiredIf(function () {
          return newComm.value.type == 'sms'
        })
      ),
      containsPlaceholders: helpers.withMessage('The SMS message contains placeholders {{ }}', value => {
        if (newComm.value.type !== 'sms') {
          return true
        }
        if (value.includes('{{')) {
          return false
        }
        return true
      })
    }
  }
}

const v$ = useVuelidate(inputRules, newComm)

watch(
  [agreement, selectedCallCenterCommsConfig],
  () => {
    initPlaceholders()
  },
  { immediate: true }
)

function applyTemplate(templateID) {
  // Find template which is to be applied
  const template = commTemplates.value.find(t => t.id === templateID)
  if (template) {
    // Template found
    if (template.type == 'email') {
      newComm.value.email.subject = template.subject
      newComm.value.email.body = template.body
    }
    if (template.type == 'sms') {
      newComm.value.sms.body = template.body
    }

    // Replace subject and body placeholders with true values
    if (template.type == 'email') {
      if (template.subject) {
        newComm.value.email.subject = replacePlaceholders(newComm.value.email.subject)
      }
      newComm.value.email.body = brToNewLine(replacePlaceholders(newComm.value.email.body))
    }
    if (template.type == 'sms') {
      newComm.value.sms.body = brToNewLine(replacePlaceholders(newComm.value.sms.body))
    }
  }
}

watch(
  () => newComm.value.email.body,
  newValue => {
    // console.log('Change', oldValue, newValue)
    newComm.value.email.body = brToNewLine(replacePlaceholders(newValue))
  }
)

const modalIsOpen = ref(false)

function replyTo(item) {
  // console.log('Replying to comm: ' + item)
  if (item.type == 'email') {
    openNewCommModal('email-reply', props.agreement, item)
  }
}

function openNewCommModal(commType, agreement, replyToItem = null) {
  // ReplyToComm should be an object of the commItem which is being replied to
  // console.log(commType)
  // console.log(agreement)
  // console.log(replyToItem)

  newComm.value.agreement = agreement
  newComm.value.replyingToBody = replyToItem?.emailBody

  // set newComm data for modal to use
  switch (commType) {
    case 'call':
      newComm.value.type = 'call'
      newComm.value.modalTitle = 'Select Number to Call'
      break
    case 'sms':
      newComm.value.type = 'sms'
      newComm.value.modalTitle = 'Send SMS'
      newComm.value.sms.from = selectedCallCenterCommsConfig.value?.smsNumber
      newComm.value.sms.to = agreement.customer.contact.mobile
      break
    case 'email':
      newComm.value.type = 'email'
      newComm.value.modalTitle = 'Send Email'
      newComm.value.email.from = selectedCallCenterCommsConfig.value?.emailAddress
      newComm.value.email.to = agreement.customer.contact.email
      break
    case 'email-reply':
      newComm.value.type = 'email'
      newComm.value.replyToItem = replyToItem
      newComm.value.email.subject = replyToItem.emailSubject
      newComm.value.modalTitle = 'Reply To ' + replyToItem.emailSubject
      newComm.value.email.from = selectedCallCenterCommsConfig.value?.emailAddress
      newComm.value.email.to = agreement.customer.contact.email
      break

    default:
      newComm.value.modalTitle = 'New Conversation'
      break
  }

  newCommInitState.value = JSON.parse(JSON.stringify(newComm.value))

  modalIsOpen.value = true
}

const aiEmailResponderApiRequest = useApiRequest()

function getAiEmailResponse(agreementId, emailBody) {
  aiEmailResponderApiRequest
    .send({
      endpoint: '/v1/ai/email-responder',
      method: 'POST',
      data: {
        agreementId: agreementId,
        email: emailBody,
        senderName: authStore.details.name,
        dealershipName: props.agreement.dealership.name,
        dealershipPhone: props.agreement.dealership.commsConfig[0].phoneNumber
      }
    })
    .then(response => {
      if (response.success) {
        // Set the input email body to the AI response
        newComm.value.email.body = response.data.reply
        // console.log(response.data.reply)
      } else {
        alertToast('Error', response.message, 'error')
      }
    })
}

emitter.on('agreement-new-conversation', commType => {
  // *Listen* for event
  openNewCommModal(commType, props.agreement)
})

function closeModal() {
  // Close the modal if all is ok
  v$.value.$reset()
  modalIsOpen.value = false
  resetNewCommObject()
}

function resetNewCommObject() {
  Object.assign(newComm.value, { type: null, call: {}, email: {}, sms: {} })
}

async function submitNewComm() {
  // console.log('Submitting new communication item')
  const isFormCorrect = await v$.value.$validate()

  if (!isFormCorrect) {
    return
  }
  newComm.value.isSending = true

  if (newComm.value.type == 'email') {
    const data = {
      agreementId: props.agreement.id,
      from: {
        email: newComm.value.email.from,
        name: props.agreement.dealership.name
      },
      to: {
        email: newComm.value.email.to,
        name: props.agreement.customer.name
      },
      subject: newComm.value.email.subject,
      body: textToHtml(newComm.value.email.body),
      callCenterId: callCenterStore.selectedCallCenter
    }

    // Check if this email is in reply to another email
    if (newComm.value.replyToItem) {
      data.inReplyTo = {
        messageId: newComm.value.replyToItem.emailMessageID,
        threadReferences: newComm.value.replyToItem.emailReferences
      }
    }

    const jsonData = JSON.stringify(data)
    // console.log(jsonData)
    try {
      emailApiRequest.send({ endpoint: '/v1/comms/create/email', method: 'POST', data: jsonData }).then(response => {
        newComm.value.isSending = false

        if (!response.success) {
          alertToast('Email failed to send', response.message, 'error')
          return
        }

        alertToast('Email Sent', null, 'success')

        // Emit to parent, to add the new communication to the array
        emit('new-conversation-submitted', data)
        closeModal()
        resetNewCommObject()
      })
    } catch (err) {
      newComm.value.isSending = false
      alertToast('Email failed to send', err.message, 'error')
    }
  }
  if (newComm.value.type == 'sms') {
    const data = {
      agreementId: props.agreement.id,
      from: newComm.value.sms.from,
      to: newComm.value.sms.to,
      body: newComm.value.sms.body,
      callCenterId: callCenterStore.selectedCallCenter
    }

    const jsonData = JSON.stringify(data)
    // console.log(jsonData)
    try {
      smsApiRequest.send({ endpoint: '/v1/comms/create/sms', method: 'POST', data: jsonData }).then(response => {
        newComm.value.isSending = false

        if (!response.success) {
          alertToast('Failed to send SMS', response.message, 'error')
          return
        }

        alertToast('SMS Sent', null, 'success')

        // Emit to parent, to add the new communication to the array
        emit('new-conversation-submitted', data)
        closeModal()
        resetNewCommObject()
      })
    } catch (err) {
      newComm.value.isSending = false
      alertToast('SMS failed to send', err.message, 'error')
    }
  }
}

watch(newComm.value, () => {
  if (JSON.stringify(newComm.value) != JSON.stringify(newCommInitState.value)) {
    newCommHasUnsavedChanges.value = true
  } else {
    newCommHasUnsavedChanges.value = false
  }
})
</script>

<style>
.ql-container {
  height: 200px;
}
</style>
